import {
  TURN_INTENTS,
  TURN_ROLES,
  type QuestionTurnResult,
  type ResponderTurn,
  type ResponderTurnPrompt,
} from '@/shared-responder-v2-core';

import {
  toThreadActions,
  type RuleActionsPayload,
} from './shared-responder-v2-gateway-action.helpers';

/**
 * `isFromAssistant` is carried separately rather than derived from
 * `!isFromResponder`, because the backend has a third sender: `system`. A
 * system note renders in the assistant's place but is never the echoed
 * question prompt, so collapsing the two flags would silently drop it when it
 * happens to lead the conversation.
 */
export interface TurnMessagePayload {
  readonly id: string;
  readonly text: string;
  readonly transcript?: string | null;
  readonly isFromResponder: boolean;
  readonly isFromAssistant: boolean;
  readonly isAssistantQuestion: boolean;
  readonly prompt: ResponderTurnPrompt | null;
}

export interface QuestionTurnPayload {
  readonly isQuestionComplete: boolean;
  readonly isConversationOver: boolean;
  readonly surveyProgress: number;
  readonly sent?: TurnMessagePayload | null;
  readonly replyFeedback?: TurnMessagePayload | null;
  readonly reply?: TurnMessagePayload | null;
  readonly nextQuestion?: {
    readonly id: string;
    readonly required?: boolean | null;
  } | null;
  readonly thankYouMessage?: { readonly text: string } | null;
  readonly actions?: RuleActionsPayload | null;
}

function toResponderTurn(
  message: TurnMessagePayload | null | undefined,
  intent: ResponderTurn['intent']
): ResponderTurn | null {
  if (!message) {
    return null;
  }

  return {
    id: message.id,
    role: message.isFromResponder ? TURN_ROLES.Responder : TURN_ROLES.Assistant,
    intent,
    text: message.text,
    transcript: message.transcript ?? null,
    prompt: message.prompt,
  };
}

function resolveNextQuestionRequired(
  nextQuestion: QuestionTurnPayload['nextQuestion']
): Pick<QuestionTurnResult, 'nextQuestionIsRequired'> {
  return typeof nextQuestion?.required === 'boolean'
    ? { nextQuestionIsRequired: nextQuestion.required }
    : {};
}

/**
 * Shared by `addQuestionMessage` and `skipQuestion` — both backend
 * mutations return the same turn shape (`addQuestionMessage`'s response
 * additionally echoes `sent`, which `skipQuestion` never carries).
 */
export function toQuestionTurnResult(
  payload: QuestionTurnPayload
): QuestionTurnResult {
  return {
    sent: toResponderTurn(payload.sent, TURN_INTENTS.PrimaryAnswer),
    replyFeedback: toResponderTurn(
      payload.replyFeedback,
      TURN_INTENTS.ClarificationReply
    ),
    reply: toResponderTurn(payload.reply, TURN_INTENTS.FollowUpQuestion),
    actions: toThreadActions(payload.actions),
    nextQuestionId: payload.nextQuestion?.id ?? null,
    ...resolveNextQuestionRequired(payload.nextQuestion),
    isComplete: payload.isQuestionComplete,
    surveyProgress: payload.surveyProgress,
    isSurveyComplete: payload.isConversationOver,
    thankYouText: payload.thankYouMessage?.text ?? null,
  };
}

export function toPriorTurns(
  messages: readonly TurnMessagePayload[]
): readonly ResponderTurn[] {
  return messages
    .filter(
      (message, index) =>
        !isEchoedQuestionPrompt(message, index) &&
        !isEmptyResponderMessage(message)
    )
    .map((message) => ({
      id: message.id,
      role: message.isFromResponder
        ? TURN_ROLES.Responder
        : TURN_ROLES.Assistant,
      intent: resolvePriorTurnIntent(message),
      text: message.text,
      transcript: message.transcript ?? null,
      prompt: message.prompt,
    }));
}

/**
 * The backend seeds a question's conversation with the question's own text as
 * an assistant message. The responder already renders the question above the
 * thread, so replaying it would show it twice.
 */
function isEchoedQuestionPrompt(
  message: TurnMessagePayload,
  index: number
): boolean {
  return index === 0 && message.isFromAssistant && !message.isAssistantQuestion;
}

/**
 * A Task's primary answer is posted with `content: ''` — nothing for a
 * resumed thread to render as a bubble.
 */
function isEmptyResponderMessage(message: TurnMessagePayload): boolean {
  return (
    message.isFromResponder &&
    message.text === '' &&
    (message.transcript ?? '') === ''
  );
}

export function hasEmptyResponderMessage(
  messages: readonly TurnMessagePayload[]
): boolean {
  return messages.some(isEmptyResponderMessage);
}

export function orderMessagesChronologically<
  T extends { readonly createdAt: string },
>(messages: readonly T[]): T[] {
  return messages.toSorted(
    (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)
  );
}

/**
 * The id `toPriorTurns` drops off the head of the same message list. Kept
 * separate rather than folded into `toPriorTurns` itself so a gateway can
 * still hand `LoadQuestionMessagesResult.promptMessageId` to the recording
 * timeline even though the message it names never reaches `turns`.
 */
export function resolvePromptMessageId(
  messages: readonly TurnMessagePayload[]
): string | null {
  const [firstMessage] = messages;

  if (firstMessage === undefined || !isEchoedQuestionPrompt(firstMessage, 0)) {
    return null;
  }

  return firstMessage.id;
}

function resolvePriorTurnIntent(
  message: TurnMessagePayload
): ResponderTurn['intent'] {
  if (message.isFromResponder) {
    return TURN_INTENTS.PrimaryAnswer;
  }

  return message.isAssistantQuestion
    ? TURN_INTENTS.FollowUpQuestion
    : TURN_INTENTS.ClarificationReply;
}
